import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { faBan, faBuilding, faLock } from '@fortawesome/free-solid-svg-icons';
import { first, mergeWith, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigInstanceToken,
    ConfigInterface,
    ConnectivityService,
    CorporateInformationComponent,
    DeviceLockManagementModalComponent,
    DeviceLockService,
    EventService,
    MenuEntryModel,
    ModalClass,
    TimesheetAndExpensesInstanceToken,
    TimesheetAndExpensesInterface,
    TimesheetService,
    UserMasterInstanceToken,
    UserMasterInterface,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PhysicalInventoryService } from './physical-inventory/services/physical-inventory.service';
import { AutomaticFormsHandoverService } from '../../services/automatic-forms-handover/automatic-forms-handover.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'pace-app-header',
    templateUrl: './app-header.component.html',
    styleUrls: ['./app-header.component.scss'],
    standalone: false,
})
export class AppHeaderComponent implements OnInit {
    public readonly enableNgRx = environment.enableNgRx;
    public faLock = faLock;
    public faBan = faBan;
    public displaySync = true;
    public isStartWorkAlreadySet$: Observable<boolean>;
    public displayWeeklyWorkTime: boolean;
    private faBuilding = faBuilding;
    public corporateInformationEntry: MenuEntryModel;
    public showCorporateEntry: boolean;
    public isPIScheduleActive$: Observable<boolean>;
    public isOnline$ = this.connectivityService.isOnline$;

    public constructor(
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly router: Router,
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly eventService: EventService,
        private readonly destroyRef: DestroyRef,
        private readonly physicalInventoryService: PhysicalInventoryService,
        private readonly timesheetService: TimesheetService,
        private readonly deviceLockService: DeviceLockService,
        private readonly connectivityService: ConnectivityService,
        private readonly automaticFormsHandoverService: AutomaticFormsHandoverService,
    ) {
        if (this.userMasterService.isPaceOnlineUser()) {
            this.router.events
                .pipe(
                    filter((event) => event instanceof NavigationEnd),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(() => {
                    this.displaySync = this.router.url.indexOf(ConfigConstants.routeUrlConfirmation) < 0;
                });
        }

        this.displayWeeklyWorkTime =
            !this.userMasterService.isPaceOnlineUser() &&
            this.configService.getDisplayWeeklyWorkTime() &&
            this.timesheetService.isTimesheetFeatureEnabled();

        this.corporateInformationEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_CORPORATE_INFORMATION_LABEL'),
            () => this.openCorporateInfo(),
            this.faBuilding,
        );

        this.showCorporateEntry = this.userMasterService.isPaceOnlineUser() || this.clientManager.isNgmP58();
    }

    public ngOnInit(): void {
        this.isPIScheduleActive$ = this.physicalInventoryService.activePISchedule$.pipe(distinctUntilChanged());
        this.isStartWorkAlreadySet$ = this.timesheetAndExpensesService.getStartOfWork$().pipe(
            map((startOfWork) => startOfWork.isValid()),
            mergeWith(this.eventService.startWorkUpdated$),
        );
    }

    public releaseLock(): void {
        this.modalService.show(DeviceLockManagementModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'app-notification-modal-title',
            keyboard: false,
            initialState: {
                releaseLockCleanupHook$: this.automaticFormsHandoverService.handOverFormsOnCheckout(),
            },
            closeInterceptor: () => {
                return new Promise((resolve) => {
                    this.deviceLockService.lockState$
                        .pipe(
                            first((lockState) => lockState.ownedByMe),
                            takeUntilDestroyed(this.destroyRef),
                        )
                        .subscribe(() => {
                            resolve();
                        });
                });
            },
            ignoreBackdropClick: true,
        });
    }

    private openCorporateInfo(): void {
        this.modalService.show(CorporateInformationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'corporate-information-modal-title',
        });
    }
}
